/**
 * Props that rest on a surface — the vanity top, and the tray that then
 * becomes a surface of its own.
 *
 * The measuring is NOT reimplemented: `topOccupiedIntervals` answers "what is
 * already standing on this host" and `freeZonesFromOccupied` turns that into
 * gaps, exactly as the dorm surface planner uses them. What is different here
 * is the policy — a short ordered wish list packed into one free run, rather
 * than a round-robin deal across many hosts — because a bathroom has one
 * vanity and the literature wants a single composed vignette on it, not decor
 * spread evenly over every surface.
 */

const { footprintFor } = require('../../geometry/objectFootprint');
const {
  freeZonesFromOccupied,
} = require('../../dorm-optimizer-v2/placement/geometry-gaps');
const {
  topOccupiedIntervals,
} = require('../../dorm-styling/plan/planSurfaceItems');
const {
  objectWorldFromLocal,
  objectLocalX,
} = require('../../dorm-styling/plan/shared');
const { stagingPropDimsM } = require('../propDims');
const { isStagingDecor, objectTypeOf } = require('../ownership');
const { isSink } = require('../../sink-types');

/**
 * Basins, for the purposes of "is this a hole in the counter".
 *
 * `isSink` is the canonical predicate and matches `sink` / `kitchen-sink`, but
 * NOT `double-sink` — and `double-sink` is not legacy spelling, it is a type
 * this repo's own bathroom analyzer CREATES
 * (`bathroom-analyzer/pipeline/types.js` declares it `AutoCreatedFixtureType`
 * at 48 x 20 in). Leaving it out un-voids exactly the two-basin vanity this
 * whole change was written for. The corpus has none today, so no test would
 * have caught it.
 *
 * ONE predicate, used for the void AND for `near: 'sink'`: two is how the soap
 * ends up anchored in a bowl that was never voided.
 */
const EXTRA_BASIN_TYPES = new Set([
  'double-sink',
  'bathroom-sink',
  'wall-hung-sink',
]);
const isBasinLike = (obj) =>
  isSink(obj) || EXTRA_BASIN_TYPES.has(objectTypeOf(obj));
const { objectsAreTouching } = require('../../geometry/objectsTouch');
const {
  SURFACE_HOST_TYPES,
  SURFACE_GAP_M,
  SURFACE_EDGE_MARGIN_M,
  SURFACE_EDGE_MARGIN_FRACTION,
  SURFACE_VOID_PAD_M,
  MAX_ANCHOR_DISTANCE_M,
  FRONT_FACING_PROPS,
  surfaceTopY,
} = require('./constants');

const SPAN_EPS = 1e-6;

/** The surfaces in this room a prop could stand on, best first. */
function findSurfaceHosts(room) {
  const objects = room?.objects || [];
  const ranked = [];
  for (const host of objects) {
    const type = objectTypeOf(host);
    const rank = SURFACE_HOST_TYPES.indexOf(type);
    if (rank < 0) continue;
    if (!host?.center || !host?.dimensions) continue;
    if (!(host.dimensions.x > 0) || !(host.dimensions.z > 0)) continue;
    ranked.push({ host, rank });
  }
  // Widest first within a rank: the roomiest vanity takes the vignette.
  // A counter that carries a BASIN outranks a wider one that does not.
  //
  // The vignette belongs on the vanity. Ranking on width alone put it on a
  // laundry counter at the back of one corpus bathroom — wider than the vanity
  // and nowhere near the sink — which reads as props abandoned on a worktop
  // rather than a styled basin. Width only breaks ties within a kind.
  const longest = (h) => Math.max(h.dimensions.x || 0, h.dimensions.z || 0);
  const basins = (room?.objects || []).filter(
    (o) => isBasinLike(o) && o?.center && o?.dimensions
  );
  // `objectsAreTouching` validates the host itself; a rotated footprint was
  // being built here only to be null-checked and thrown away.
  const carriesBasin = (host) =>
    basins.some((b) => objectsAreTouching(host, b, 0.05));
  for (const entry of ranked) entry.basin = carriesBasin(entry.host) ? 0 : 1;
  ranked.sort(
    (a, b) =>
      a.basin - b.basin || a.rank - b.rank || longest(b.host) - longest(a.host)
  );
  return ranked.map((r) => r.host);
}

/**
 * The host, re-expressed so its LONG horizontal axis is local +x.
 *
 * Everything in this file packs along local x. That is the long axis for most
 * countertops, but not all: `resolveCounterContainer` records that 60 of 331
 * dev vanity pairs store the countertop at ry = 0 while its cabinet is at
 * +/-90, so the countertop's `dimensions.z` is its WIDTH. A narrow
 * powder-room vanity is the same shape of problem for an honest reason.
 *
 * Refusing those hosts was the first answer and it was wrong: it silently cost
 * a room every surface prop, and it could not tell an axis-swapped counter
 * from a genuinely narrow one. Packing along whichever axis is longer answers
 * both.
 *
 * Local +x of a host rotated by `r - PI/2` is exactly local +z of a host
 * rotated by `r`, so swapping the dimensions and turning the frame a quarter
 * back is a faithful re-expression, not an approximation. The returned host
 * keeps the real one's `id` and `type`, so `parents` is unaffected.
 */
function longAxisHost(host) {
  const d = host?.dimensions;
  if (!d || (d.x || 0) >= (d.z || 0)) return host;
  return {
    ...host,
    dimensions: { ...d, x: d.z, z: d.x },
    rotation: {
      ...(host.rotation || {}),
      y: (host.rotation?.y || 0) - Math.PI / 2,
    },
  };
}

/**
 * An object's box as {x, y, z}, reading `width`/`height`/`depth` where the
 * axis fields are missing: `sink-types` documents six dimension shapes and 9
 * of 99 corpus sinks carry only `{width, depth, height}`. Missing stays
 * undefined — callers decide what an unknown size means.
 */
function planDims(obj) {
  const d = obj?.dimensions || {};
  const pick = (a, b) =>
    Number.isFinite(d[a]) ? d[a] : Number.isFinite(d[b]) ? d[b] : undefined;
  return {
    x: pick('x', 'width'),
    y: pick('y', 'height'),
    z: pick('z', 'depth'),
  };
}

/**
 * The span an object actually covers along the host's own run, computed from
 * its ROTATED footprint.
 *
 * NOT `max(dims.x, dims.z) / 2`, which `topOccupiedIntervals` uses and which an
 * earlier version of this file copied while calling it "conservative". It is
 * conservative only when the object's yaw differs from the host's by a
 * multiple of 90 degrees. At 45 degrees a 19.8 x 15.6 in basin really spans
 * 12.52 in of the run against that measure's 9.90 — it UNDER-reports by 2.6 in,
 * so the void comes out too narrow and a prop lands in the bowl. Projecting
 * the four corners is exact at every angle and costs nothing.
 *
 * Falls back to `width`/`depth`: `sink-types` documents six dimension shapes
 * and 9 of 99 corpus sinks carry only `{width, depth, height}`. Without the
 * fallback those yield NaN, and ONE NaN interval sets `freeZonesFromOccupied`'s
 * cursor to NaN and silently destroys every zone after it.
 */
function runIntervalFor(host, obj, hostW) {
  const { x: w, z: depth } = planDims(obj);
  if (!(w > 0) || !(depth > 0)) return null;
  const corners = footprintFor(obj.center, w, depth, obj?.rotation?.y || 0);
  if (!corners) return null;
  const ts = corners.map((c) => objectLocalX(host, c) + hostW / 2);
  if (!ts.every(Number.isFinite)) return null;
  const start = Math.max(0, Math.min(...ts));
  const end = Math.min(hostW, Math.max(...ts));
  return end - start > SPAN_EPS ? { start, end } : null;
}

/**
 * Intervals of the host's run taken up by VOIDS — holes in the surface rather
 * than things standing on it. A basin is the case.
 *
 * `allObjects`, deliberately not the `standing` list the occupier pass uses:
 * 51 of 63 corpus basins are wall-parented and would be filtered out before
 * anything looked at them. Wall-parenting says where a thing is FIXED; it says
 * nothing about whether it is a hole in this counter.
 *
 * `isVoid` defaults to the basin test. A kitchen also passes cooktops: they
 * sit ON the slab rather than in it, but nothing may stand on one either, and
 * they are wall-parented, so the occupier pass never sees them.
 */
function surfaceVoidIntervals(host, allObjects, hostW, isVoid = isBasinLike) {
  const out = [];
  for (const obj of allObjects || []) {
    if (!obj || obj === host) continue;
    if (!obj.center || !obj.dimensions) continue;
    if (!isVoid(obj)) continue;
    // Set INTO this counter, rather than merely near it. The rotated footprint
    // plus vertical interval is the test that already gets an undermount right.
    if (!objectsAreTouching(host, obj, 0.02)) continue;
    const interval = runIntervalFor(host, obj, hostW);
    if (interval) out.push(interval);
  }
  return out;
}

/**
 * The free runs on a host's top, each flagged with whether its ends are the
 * host's own PHYSICAL ends or an internal boundary.
 *
 * That distinction is the whole point. `SURFACE_EDGE_MARGIN_M` exists to stop a
 * prop overhanging the counter's outer edge, where it would fall off. Spending
 * it at a basin boundary too burns 4 in out of the middle of every counter —
 * measured, that alone was the difference between 4.1 in and 6.1 in of median
 * usable run on the 47 corpus bathrooms that have a surface host.
 * Internally the right margin is the rim pad, which is already baked into the
 * intervals below.
 */
/**
 * Placement options a room planner may pass; all default to the bathroom's
 * behaviour, which is what keeps that output byte-identical.
 *   - `isVoid`  which objects are holes in the surface (default: basins);
 *   - `blocked` extra run intervals no prop may enter, already padded by the
 *               caller (a kitchen's prep zone, or a stretch too low under an
 *               upper cabinet for the prop at hand);
 *   - `depth`   where across the surface a prop sits: 'back' (default; half
 *               the spare depth toward the back), 'against-back' (flush to
 *               the back edge, for a prop that leans on the backsplash),
 *               'back-third', 'front-half', or 'centre' (an island, which has no back).
 */
function freeRunsOnHost(
  host,
  hostCorners,
  hostTop,
  allObjects,
  hostW,
  opts = {}
) {
  const standing = (allObjects || []).filter(
    (o) => !(o?.parentWallIds?.length > 0)
  );
  const occupied = topOccupiedIntervals(host, hostCorners, hostTop, standing);
  const voids = surfaceVoidIntervals(host, allObjects, hostW, opts.isVoid);
  // Pad every blocker, occupier and void alike: the rim of a basin and the
  // base of a faucet want the same breathing room, and 57 of 57 corpus faucets
  // are already occupiers, so a separate faucet rule would buy only this pad.
  const blocked = [...occupied, ...voids]
    .filter((iv) => Number.isFinite(iv?.start) && Number.isFinite(iv?.end))
    .map((iv) => ({
      start: iv.start - SURFACE_VOID_PAD_M,
      end: iv.end + SURFACE_VOID_PAD_M,
    }));
  for (const iv of opts.blocked || []) {
    if (Number.isFinite(iv?.start) && Number.isFinite(iv?.end))
      blocked.push(iv);
  }
  return freeZonesFromOccupied(blocked, hostW).map((zone) => ({
    ...zone,
    startIsPhysicalEnd: zone.start <= SPAN_EPS,
    endIsPhysicalEnd: zone.end >= hostW - SPAN_EPS,
  }));
}

/** Distance a prop is gap'd from the back edge when it leans on the backsplash. */
const AGAINST_BACK_GAP_M = 0.25 * 0.0254;
/**
 * A 'front-half' prop keeps at least this much counter in front of it. Without
 * it the quarter-depth offset put an 11 in fruit bowl 0.1-1 in from a 25 in
 * counter's lip in both kitchens Megan reviewed (2026-10-18).
 */
const FRONT_SETBACK_M = 4 * 0.0254;

/**
 * Local z of a prop's centre across a host `hostDepth` deep. Local -z is the
 * BACK of the surface (the wall side), +z the front edge.
 */
function depthOffset(depth, hostDepth, propDepth) {
  const spare = Math.max(0, hostDepth - propDepth);
  switch (depth) {
    case 'against-back':
      return -spare / 2 + Math.min(AGAINST_BACK_GAP_M, spare / 2);
    case 'back-third':
      return -Math.min(spare / 2, hostDepth / 3);
    case 'front-half':
      // Forward of centre (clear of the uppers over the back half), never at the lip.
      return Math.max(0, Math.min(spare / 2 - FRONT_SETBACK_M, hostDepth / 4));
    case 'centre':
      return 0;
    default:
      // Tuck toward the BACK of the surface, the way a styled vanity reads —
      // half the spare depth, so nothing sits on the front edge.
      return -spare / 4;
  }
}

/** The span of a zone a prop may actually occupy, after the right margins. */
function usableBounds(zone, hostW) {
  const edge = Math.min(
    SURFACE_EDGE_MARGIN_M,
    hostW * SURFACE_EDGE_MARGIN_FRACTION
  );
  return {
    lo: zone.start + (zone.startIsPhysicalEnd ? edge : 0),
    hi: zone.end - (zone.endIsPhysicalEnd ? edge : 0),
  };
}

/**
 * Place an ordered wish list along the largest clear run of a host's top.
 *
 * Returns `{ placed, rejected }` — what fitted, and what did not, so the
 * caller can spend its budget elsewhere rather than silently losing a prop.
 */
function placeOnSurface(realHost, wishes, scanObjects, make, opts = {}) {
  const host = longAxisHost(realHost);
  const hc = host.center;
  const hd = host.dimensions;
  const hostW = hd.x || 0;
  if (hostW <= 0) return { placed: [], rejected: wishes.slice() };

  // Not the bbox top: a tray is a vessel and its contents rest on its inner
  // floor, a lip's height below.
  const hostTop = surfaceTopY(host);
  if (hostTop == null) return { placed: [], rejected: wishes.slice() };
  const corners = footprintFor(
    hc,
    hd.x || 0,
    hd.z || 0,
    host?.rotation?.y || 0
  );
  if (!corners) return { placed: [], rejected: wishes.slice() };

  const zones = freeRunsOnHost(
    host,
    corners,
    hostTop,
    scanObjects,
    hostW,
    opts
  );

  // Every run the host offers, each with the span a prop may actually occupy.
  // A single-basin vanity has a run either side of the bowl; a two-basin one
  // has three.
  //
  // A run starts out already CARRYING whatever staged decor stands on this
  // host's same side, because balance is about the counter, not about this
  // one call. The soap is the case: `placeNearOnSurface` seats it beside the
  // basin BEFORE the row is composed, and it reaches this function only as an
  // obstacle — so the run it stands in was measured around it and then
  // counted as empty, and the row cheerfully piled up on the soap's side.
  // Measured on the corpus: 5 vanities, every one of them with the soap in
  // the group.
  //
  // Sides are split at the BASIN, not at the host's geometric midpoint. The
  // basin is what a viewer reads the counter as having two sides OF, and a
  // scanned basin is routinely off-centre — on those vanities the two
  // dividers disagree, and balancing about the midpoint leaves the decor
  // visibly bunched on one side of the bowl while the arithmetic says it is
  // even. Widest void when there are two, so a trough or a pair of basins
  // still gives one divider. No basin at all (a laundry counter) falls back
  // to the midpoint.
  //
  // STAGED props already on the host, and only those. Recognised by their
  // `parents` link — the one they carry for `moveGroup` — so no geometry is
  // re-derived and nothing on a TRAY is mistaken for something on the counter
  // under it; and by `isStagingDecor`, because a `parents` link alone is not
  // "decor". A scanned faucet is parented to its countertop too, and counting
  // it charged one corpus vanity 39 in of decor on a 46 in counter, which
  // pinned every prop to the other side — the exact fault this pre-charge
  // exists to prevent, caused by the pre-charge.
  const hostVoids = surfaceVoidIntervals(host, scanObjects, hostW, opts.isVoid);
  const widestVoid = hostVoids.length
    ? hostVoids.reduce((a, b) => (b.end - b.start > a.end - a.start ? b : a))
    : null;
  const mid = widestVoid ? (widestVoid.start + widestVoid.end) / 2 : hostW / 2;
  const standing = (scanObjects || []).filter(
    (o) =>
      isStagingDecor(o) &&
      (o?.parents || []).some((parent) => parent.id === realHost.id)
  );
  const loadOn = (side) =>
    standing
      .filter((o) => {
        const t = objectLocalX(host, o.center) + hostW / 2;
        return Number.isFinite(t) && (t < mid ? 'lo' : 'hi') === side;
      })
      .reduce((sum, o) => sum + (o.dimensions?.x || 0) + SURFACE_GAP_M, 0);
  const loadLo = loadOn('lo');
  const loadHi = loadOn('hi');

  const runs = [];
  for (const z of zones) {
    const { lo, hi } = usableBounds(z, hostW);
    if (hi - lo <= 0) continue;
    runs.push({
      lo,
      hi,
      // `carried` is decor on this side that is NOT in this run, so it biases
      // the choice without ever being charged against the run's own length.
      carried: (lo + hi) / 2 < mid ? loadLo : loadHi,
      used: 0,
      items: [],
    });
  }
  if (!runs.length) return { placed: [], rejected: wishes.slice() };

  // The vignette is composed ACROSS the runs, not packed into one.
  //
  // Packing one run was the original policy and it is what makes a styled
  // vanity look lopsided: every prop lands on one side of the basin and the
  // other side is bare. ("a lot of the time you put all the decor on one side
  // of the counter and none on the other side" — 2026-10-11.) A staged
  // bathroom composes ABOUT the basin, which is the one thing a vanity is
  // symmetrical around.
  //
  // So each prop goes to the EMPTIEST run that can still take it, measured as
  // the length of prop already standing there. That is literally "equal decor
  // either side", and it falls out of the rule rather than being special-cased
  // per vanity shape: with one run it degenerates to the old packer, which is
  // what the tray stage below wants.
  //
  // Ties go to the run nearest the host's CENTRE, and that tie-break is the
  // old single-run rule preserved: every run starts empty, so the FIRST and
  // highest-priority prop still picks exactly the run it used to. It matters
  // because the widest run touches a physical END of the counter in 32 of the
  // 45 corpus vanities that have one, which pushes the leading prop outboard,
  // and on a two-basin vanity the designer's spot is the gap BETWEEN the
  // basins.
  //
  // What this deliberately does NOT do is spread props evenly along the whole
  // counter: three items marooned across six feet is the lived-in failure, not
  // staging. Props stay shoulder-to-shoulder within whichever run they land
  // in, so each run reads as a group.
  // Deliberately the HOST's centre and not the basin divider above: this is
  // the old single-run rule preserved verbatim for the leading prop, and it
  // is about not pushing the group out to a counter END.
  const centre = hostW / 2;
  const fromCentre = (run) => Math.abs((run.lo + run.hi) / 2 - centre);
  const rejected = [];
  let accepted = 0;
  for (const wish of wishes) {
    const dims = stagingPropDimsM(wish.type);
    if (!dims) {
      rejected.push(wish);
      continue;
    }
    let best = null;
    for (const run of runs) {
      // Its own width if the run is empty, otherwise what is already there
      // plus a gap plus itself.
      const needed =
        run.used === 0 ? dims.x : run.used + SURFACE_GAP_M + dims.x;
      if (needed > run.hi - run.lo) continue;
      const load = run.used + run.carried;
      const bestLoad = best ? best.run.used + best.run.carried : 0;
      const better =
        !best ||
        load < bestLoad ||
        (load === bestLoad && fromCentre(run) < fromCentre(best.run));
      if (better) best = { run, needed };
    }
    // Keep going past a prop that fits nowhere: trying in order is what
    // honours the priority, and leaving the space empty rather than handing it
    // to a smaller prop further down the list helps nobody.
    if (!best) {
      rejected.push(wish);
      continue;
    }
    best.run.used = best.needed;
    // `order` is the priority rank, carried so the RESULT can be re-sorted
    // back into it below.
    best.run.items.push({ wish, dims, order: accepted++ });
  }
  if (!accepted) return { placed: [], rejected };

  // Lay each run out, centring its own group in its own span.
  const seated = [];
  for (const run of runs) {
    if (!run.items.length) continue;
    let cursor = (run.lo + run.hi) / 2 - hostW / 2 - run.used / 2;
    for (const { wish, dims, order } of run.items) {
      const lx = cursor + dims.x / 2;
      cursor += dims.x + SURFACE_GAP_M;
      const lz = depthOffset(opts.depth, hd.z || 0, dims.z);
      const { x, z } = objectWorldFromLocal(host, lx, lz);
      // A prop with a FRONT is turned to face the basin. Its renderer draws
      // the spout on local +x, and the host's own yaw points that wherever
      // the counter happens to run — on half of them, at the wall.
      //
      // Measured against the NEAREST basin, not against `mid`. `mid` is the
      // balance divider — one line for the counter's two sides — and on a
      // two-basin vanity it falls between the bowls, so a bottle beside the
      // far basin was turned away from it. Different question, different
      // answer: which side is emptier, versus which bowl am I serving.
      const t = lx + hostW / 2;
      const nearestVoid = hostVoids.length
        ? hostVoids.reduce((best, cur) => {
            const gap = (v) =>
              t < v.start ? v.start - t : t > v.end ? t - v.end : 0;
            return gap(cur) < gap(best) ? cur : best;
          })
        : null;
      const facesBasin =
        FRONT_FACING_PROPS.has(wish.type) &&
        nearestVoid &&
        (nearestVoid.start + nearestVoid.end) / 2 < t
          ? Math.PI
          : 0;
      seated.push({
        order,
        wish,
        fields: {
          center: { x, y: hostTop + dims.y / 2, z },
          dimensions: dims,
          rotation: { x: 0, y: (host?.rotation?.y || 0) + facesBasin, z: 0 },
          host,
        },
      });
    }
  }

  // Back into PRIORITY order before anything is minted. The caller spends a
  // budget down this list and stops when it runs out, so run order here would
  // let a garnish survive at the expense of the prop that anchors the group —
  // and the minted ids would stop following priority too.
  seated.sort((a, b) => a.order - b.order);
  return {
    placed: seated.map(({ wish, fields }) => make(wish, fields)),
    rejected,
  };
}

/**
 * Place ONE prop on a surface as close as it can get to a point — soap beside
 * the sink, which is where a bar of soap actually lives and where a buyer
 * expects to find it. Runs BEFORE the composed row: a prop that must sit
 * somewhere specific loses every race against one that will go anywhere.
 */
function placeNearOnSurface(
  realHost,
  wish,
  anchorPoint,
  scanObjects,
  make,
  opts = {}
) {
  const host = longAxisHost(realHost);
  const hc = host.center;
  const hd = host.dimensions;
  const hostW = hd.x || 0;
  if (hostW <= 0) return null;

  const hostTop = surfaceTopY(host);
  if (hostTop == null) return null;
  const corners = footprintFor(
    hc,
    hd.x || 0,
    hd.z || 0,
    host?.rotation?.y || 0
  );
  if (!corners) return null;

  const dims = stagingPropDimsM(wish.type);
  if (!dims) return null;

  const zones = freeRunsOnHost(
    host,
    corners,
    hostTop,
    scanObjects,
    hostW,
    opts
  );

  // The anchor in the host's own 1-D space, the same frame the free zones use.
  const anchorLocal = objectLocalX(host, anchorPoint) + hostW / 2;

  // The ceiling is measured from the basin's RIM, not its centre. A prop can
  // never sit closer to the centre than half the basin, so a centre-measured
  // ceiling tightens as the basin widens -- backwards. On a 36 in trough it
  // dropped the soap outright with 11 in of clear counter beside the bowl.
  const voids = surfaceVoidIntervals(host, scanObjects, hostW, opts.isVoid);
  const anchorVoid = voids.find(
    (v) => anchorLocal >= v.start && anchorLocal <= v.end
  );
  // Distance from the anchor's own INTERVAL, so a prop beside the basin's far
  // edge is not charged for the width of the basin it is beside. Measuring to
  // a single fixed rim drops the soap from whichever side of the bowl it is
  // not on.
  const fromAnchorRim = (t) => {
    if (!anchorVoid) return Math.abs(t - anchorLocal);
    if (t < anchorVoid.start) return anchorVoid.start - t;
    if (t > anchorVoid.end) return t - anchorVoid.end;
    return 0;
  };

  let best = null;
  for (const zone of zones) {
    const bounds = usableBounds(zone, hostW);
    const lo = bounds.lo + dims.x / 2;
    const hi = bounds.hi - dims.x / 2;
    if (hi < lo) continue;
    const at = Math.min(Math.max(anchorLocal, lo), hi);
    // Rank by closeness to the anchor, but GATE on closeness to its rim.
    const distance = Math.abs(at - anchorLocal);
    const fromRim = fromAnchorRim(at);
    if (!best || distance < best.distance) best = { at, distance, fromRim };
  }
  // "Beside the sink" has to mean beside it. Without a ceiling, a 60 in
  // two-basin vanity satisfies the anchor from an outboard run 30 in away and
  // the soap reads as unrelated to the basin it belongs to.
  const maxAnchor = opts.maxAnchorDistance ?? MAX_ANCHOR_DISTANCE_M;
  if (!best || best.fromRim > maxAnchor) return null;

  const lz = depthOffset(opts.depth, hd.z || 0, dims.z);
  const { x, z } = objectWorldFromLocal(host, best.at - hostW / 2, lz);
  return make(wish, {
    center: { x, y: hostTop + dims.y / 2, z },
    dimensions: dims,
    rotation: { x: 0, y: host?.rotation?.y || 0, z: 0 },
    host,
  });
}

module.exports = {
  findSurfaceHosts,
  placeOnSurface,
  placeNearOnSurface,
  surfaceVoidIntervals,
  freeRunsOnHost,
  usableBounds,
  longAxisHost,
  isBasinLike,
  runIntervalFor,
  depthOffset,
  FRONT_SETBACK_M,
  planDims,
};
